/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { useEffect, useState } from 'react';
import {
    utils as commonUtils,
    hooks as commonHooks,
    types as commonTypes,
} from '@sitebuilder/common';
import {
    getLayoutSchemaProperties,
    getStyleSchemaProperties,
    getTextSchemaProperties,
} from '../utils';
import { downloadWithQueryStyles } from './DownloadWithQuery.styles';

export interface DownloadWithQueryProps {
    dataSourceType: string;
    dataSource: string;
    query: string;
    queryVariables: string;
    buttonLabel: string;
    fileName: string;
    transformFunction: string;
    showButtonIcon?: boolean;
    buttonIconPosition?: string;
    buttonIconType?: string;
    buttonIconName?: string;
    buttonIconImageUrl?: string;
    butttonImgAltText?: string;
    showLoadingIcon?: boolean;
    loadingIconPosition?: string;
    loadingIconType?: string;
    loadingIconName?: string;
    loadingIconImageUrl?: string;
    loadingImgAltText?: string;
    showErrorMessage?: boolean;
    showErrorIcon?: boolean;
    errorIconPosition?: string;
    errorIconType?: string;
    errorIconName?: string;
    errorIconImageUrl?: string;
    errorImgAltText?: string;
    addColumns?: boolean;
    columns?: { label: string; value: string }[];
}

export interface WidgetProperties
    extends commonTypes.WidgetPropertiesBase,
        DownloadWithQueryProps {}

export interface Widgetprops extends commonTypes.WidgetInstanceProps {
    properties?: any;
}

const DownloadWithQuery = (props: Widgetprops) => {
    const appContext = commonHooks.useAppContext();
    const i18n = commonHooks.useLocalization();
    const { accessToken, originalHostname, pageName, componentName, baseUrl, graphQLClient } =
        appContext;
    const { data } = props;
    const widgetProperties: any =
        commonHooks.useWidgetProperties<WidgetProperties>(props.widgetInstanceId) ||
        props?.properties;

    const {
        styles,
        dataSourceType,
        dataSource,
        query,
        queryVariables,
        buttonLabel,
        fileName,
        transformFunction,
        transform,
        ...rest
    } = widgetProperties || props;
    const theme = props?.data?.themeSelected || props?.theme;
    const variablesStr = queryVariables as string;
    const [variables, setVariables] = useState<any>('');
    const [queryParams, setQueryParams] = useState<any>({});
    const [queryLoading, setQueryLoading] = useState<boolean>(false);
    const [queryError, setQueryError] = useState<any>(null);
    const extensions: Record<string, unknown> = {};
    const _fileName = i18n.format(fileName, data) || 'data';

    if (transform) {
        extensions.transform = transform;
    }

    const transformFunctionName = `${props.widgetInstanceId}-queryVariables-transform`;
    // Added initialVariable in case state is not update for queryParams variables
    const initialVariable = commonUtils.transformDataWithGlobals(
        transformFunctionName,
        variablesStr,
        data
    );

    useEffect(() => {
        try {
            if (typeof variablesStr === 'string' && variablesStr.length > 0) {
                setVariables(
                    commonUtils.transformDataWithGlobals(transformFunctionName, variablesStr, data)
                );
            }
        } catch (e) {
            console.log(e);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [variablesStr]);

    useEffect(() => {
        setQueryParams({
            dataSourceType: dataSourceType,
            dataSetName: dataSource,
            authorizationToken: accessToken as string,
            query,
            variables: variables || initialVariable,
            extensions,
            originalHostname,
            fileName: _fileName,
            transformFunction,
            columns: rest?.addColumns ? rest?.columns : data?.downloadFileColumns || [],
            documentName: pageName,
            componentName: componentName,
            baseUrl,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        accessToken,
        dataSource,
        dataSourceType,
        originalHostname,
        query,
        variables,
        data?.downloadFileColumns,
    ]);

    const executeQuery = async (graphqlQueryParams: any) => {
        try {
            const queryApiClient = graphQLClient;
            const graphQLQueryResult = await queryApiClient.downloadWithQuery(graphqlQueryParams);
            handleDownloadCSV(graphQLQueryResult);
            setQueryLoading(false);
        } catch (err: any) {
            setQueryLoading(false);
            setQueryError(err);
            return null;
        }
    };

    const handleDownloadButtonClick = async () => {
        setQueryLoading(true);
        await executeQuery(queryParams);
    };

    const handleDownloadCSV = (csvData: any) => {
        if (csvData) {
            const BOM = '\uFEFF';
            const blob = new Blob([BOM + csvData], { type: 'text/csv' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `${_fileName}.csv`);
            link.setAttribute('target', '_blank');
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
    };

    const renderIcon = (
        iconType: string,
        iconName: string,
        iconClassName: string,
        iconImageUrl: string,
        iconImageClassName: string,
        altImgTxt: string
    ) => {
        return (
            <>
                {iconType === 'FontAwesome Icon' ? (
                    <i className={`${iconClassName || ''} fas ${iconName}`} />
                ) : iconType === 'Image' ? (
                    <img
                        className={iconImageClassName || ''}
                        src={iconImageUrl}
                        alt={altImgTxt || 'icon'}
                    />
                ) : null}
            </>
        );
    };

    return (
        <div
            css={css`
                ${!theme ? '' : downloadWithQueryStyles}${getTextSchemaProperties(
                    widgetProperties,
                    data?.colorSpace
                )}${getStyleSchemaProperties(widgetProperties)}${getLayoutSchemaProperties(
                    widgetProperties
                )}${styles || ''}
            `}
            className={`download-with-query ${
                widgetProperties?.typographyClass ? widgetProperties.typographyClass : ''
            }`}
        >
            <button className="download-button" onClick={handleDownloadButtonClick}>
                {queryLoading &&
                    rest?.showLoadingIcon &&
                    rest?.loadingIconPosition === 'Left' &&
                    renderIcon(
                        rest.loadingIconType,
                        rest.loadingIconName,
                        'loading-icon',
                        rest.loadingIconImageUrl,
                        'loading-image',
                        rest.loadingImgAltText
                    )}
                {rest?.showButtonIcon &&
                    rest?.buttonIconPosition === 'Left' &&
                    renderIcon(
                        rest.buttonIconType,
                        rest.buttonIconName || 'fa-solid fa-download',
                        'button-icon',
                        rest.buttonIconImageUrl,
                        'button-image',
                        rest.butttonImgAltText
                    )}
                {i18n.format(buttonLabel, data) || 'Download'}
                {rest?.showButtonIcon &&
                    rest?.buttonIconPosition === 'Right' &&
                    renderIcon(
                        rest.buttonIconType,
                        rest.buttonIconName,
                        'button-icon',
                        rest.buttonIconImageUrl,
                        'button-image',
                        rest.butttonImgAltText
                    )}
                {rest?.showLoadingIcon && rest?.loadingIconPosition === 'Right' && (
                    <div className="loading-icon">
                        {queryLoading
                            ? renderIcon(
                                  rest.loadingIconType,
                                  rest.loadingIconName || 'fa-circle-notch fa-spin fa-xs',
                                  'loading-icon',
                                  rest.loadingIconImageUrl,
                                  'loading-image',
                                  rest.loadingImgAltText
                              )
                            : null}
                    </div>
                )}
            </button>
            {rest.showErrorMessage && queryError?.message ? (
                <span className="download-error-message">
                    {rest?.errorIconPosition === 'Left' &&
                        renderIcon(
                            rest.errorIconType,
                            rest.errorIconName || 'fa-exclamation-circle',
                            'error-icon',
                            rest.errorIconImageUrl,
                            'error-image',
                            rest.errorImgAltText
                        )}
                    {queryError?.message}
                    {rest?.errorIconPosition === 'Right' &&
                        renderIcon(
                            rest.errorIconType,
                            rest.errorIconName || 'fa-exclamation-circle',
                            'error-icon',
                            rest.errorIconImageUrl,
                            'error-image',
                            rest.errorImgAltText
                        )}
                </span>
            ) : null}
        </div>
    );
};

export default DownloadWithQuery;
